/* ImageCropper — the square crop step every uploaded image goes through.
 *
 * It is an antd `<Modal>` now, not a hand-rolled scrim: the modal chrome, the focus trap, Esc, and
 * the button pair come from the kit (spec/02), and the shared modal rules apply — no boxed chrome
 * inside the body, the actions in the footer.
 *
 * **The selection is always a square, and that is a decision, not a limitation.** The result is
 * rendered into square avatars (the record's identity strip) and a 40px table thumbnail, so a
 * free-ratio crop would only be cropped again — badly — by everything that displays it.
 *
 * What it can do beyond dragging, because a mouse-only editor is an unusable one (spec/03's
 * accessibility floor):
 * - **Zoom** — a slider over the selection's side length, plus `+` / `-`. Zooming in shrinks the
 *   square around its own centre, which is what "closer" means when the image is fixed.
 * - **Arrow keys** nudge the selection by 1px, or 10px with Shift; Home re-centres it.
 * - **A live 1:1 preview**, drawn with `background-position` rather than a second canvas — the
 *   point of cropping is seeing the framing you are choosing.
 *
 * The geometry (display ↔ natural scaling, corner anchoring, bounds clamping) is unchanged from the
 * version that worked; only the chrome and the input methods are new.
 */
import React, { Component } from "react";
import { Flex, Modal, Slider, Typography } from "antd";
import { ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

const MIN_SIDE = 24;
const NUDGE = 1;
const NUDGE_FAR = 10;
const ZOOM_STEP = 8;

export default class ImageCropper extends Component {
  constructor(props) {
    super(props);
    this.imgRef = React.createRef();
    this.selRef = React.createRef();
    this.state = { url: URL.createObjectURL(props.file), natural: null, display: null, sel: null, drag: null };
  }

  componentWillUnmount() {
    if (this.state.url) URL.revokeObjectURL(this.state.url);
  }

  onImgLoad = () => {
    const img = this.imgRef.current;
    const display = { w: img.clientWidth, h: img.clientHeight };
    const natural = { w: img.naturalWidth, h: img.naturalHeight };
    // Square selection (1:1), centered, sized to 60% of the shorter side.
    const side = Math.round(Math.min(display.w, display.h) * 0.6);
    this.setState(
      {
        natural,
        display,
        sel: { x: Math.round((display.w - side) / 2), y: Math.round((display.h - side) / 2), w: side, h: side },
      },
      /* Focus the selection, not the footer: arrow keys are the keyboard path into this editor, and
         they only reach it while it holds focus. */
      () => this.selRef.current && this.selRef.current.focus()
    );
  };

  /** The largest square that still fits the image. */
  get maxSide() {
    const { display } = this.state;
    return display ? Math.min(display.w, display.h) : MIN_SIDE;
  }

  /** Resize around the selection's centre, then push it back inside the image. */
  setSide = nextSide => {
    const { sel, display } = this.state;
    if (!sel || !display) return;
    const side = clamp(Math.round(nextSide), MIN_SIDE, this.maxSide);
    const cx = sel.x + sel.w / 2;
    const cy = sel.y + sel.h / 2;
    this.setState({
      sel: {
        x: clamp(Math.round(cx - side / 2), 0, display.w - side),
        y: clamp(Math.round(cy - side / 2), 0, display.h - side),
        w: side,
        h: side,
      },
    });
  };

  move = (dx, dy) => {
    const { sel, display } = this.state;
    if (!sel || !display) return;
    this.setState({
      sel: { ...sel, x: clamp(sel.x + dx, 0, display.w - sel.w), y: clamp(sel.y + dy, 0, display.h - sel.h) },
    });
  };

  onKeyDown = event => {
    const { sel, display } = this.state;
    if (!sel || !display) return;
    const step = event.shiftKey ? NUDGE_FAR : NUDGE;
    const keys = {
      "ArrowLeft": () => this.move(-step, 0),
      "ArrowRight": () => this.move(step, 0),
      "ArrowUp": () => this.move(0, -step),
      "ArrowDown": () => this.move(0, step),
      "+": () => this.setSide(sel.w - ZOOM_STEP),
      "=": () => this.setSide(sel.w - ZOOM_STEP),
      "-": () => this.setSide(sel.w + ZOOM_STEP),
      "_": () => this.setSide(sel.w + ZOOM_STEP),
      "Home": () => this.centre(),
      "Enter": () => this.confirm(),
    };
    const handler = keys[event.key];
    if (!handler) return;
    /* Arrow keys scroll the modal body otherwise, so the selection appears not to move. */
    event.preventDefault();
    handler();
  };

  centre = () => {
    const { sel, display } = this.state;
    if (!sel || !display) return;
    this.setState({ sel: { ...sel, x: Math.round((display.w - sel.w) / 2), y: Math.round((display.h - sel.h) / 2) } });
  };

  startDrag = (mode, e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ drag: { mode, startX: e.clientX, startY: e.clientY, orig: { ...this.state.sel } } });
  };

  onMove = e => {
    const { drag, display } = this.state;
    if (!drag || !display) return;
    const dx = e.clientX - drag.startX,
      dy = e.clientY - drag.startY;
    const o = drag.orig;

    // Move: shift the square within the image bounds (size unchanged).
    if (drag.mode === "move") {
      const x = clamp(o.x + dx, 0, display.w - o.w);
      const y = clamp(o.y + dy, 0, display.h - o.h);
      this.setState({ sel: { x, y, w: o.w, h: o.h } });
      return;
    }

    // Corner resize: keep a perfect square (1:1). The opposite corner stays fixed,
    // and the side grows/shrinks with the drag, clamped to the image bounds.
    let grow, maxSide;
    if (drag.mode === "se") {
      grow = Math.max(dx, dy);
      maxSide = Math.min(display.w - o.x, display.h - o.y);
    } else if (drag.mode === "nw") {
      grow = Math.max(-dx, -dy);
      maxSide = Math.min(o.x + o.w, o.y + o.h);
    } else if (drag.mode === "ne") {
      grow = Math.max(dx, -dy);
      maxSide = Math.min(display.w - o.x, o.y + o.h);
    } else {
      grow = Math.max(-dx, dy);
      maxSide = Math.min(o.x + o.w, display.h - o.y);
    } // sw

    const side = clamp(o.w + grow, MIN_SIDE, maxSide);
    let x = o.x,
      y = o.y; // se anchors at top-left
    if (drag.mode === "nw") {
      x = o.x + o.w - side;
      y = o.y + o.h - side;
    } else if (drag.mode === "ne") {
      y = o.y + o.h - side;
    } else if (drag.mode === "sw") {
      x = o.x + o.w - side;
    }

    this.setState({ sel: { x, y, w: side, h: side } });
  };

  endDrag = () => {
    if (this.state.drag) this.setState({ drag: null });
  };

  confirm = () => {
    const { natural, display, sel } = this.state;
    if (!sel || !display || !natural) return;
    const sx = (sel.x / display.w) * natural.w,
      sy = (sel.y / display.h) * natural.h;
    const sw = (sel.w / display.w) * natural.w,
      sh = (sel.h / display.h) * natural.h;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(sw));
    canvas.height = Math.max(1, Math.round(sh));
    canvas.getContext("2d").drawImage(this.imgRef.current, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    const type = this.props.file.type || "image/png";
    canvas.toBlob(
      blob => {
        const cropped = new File([blob], this.props.file.name || "logo.png", { type });
        this.props.onConfirm(cropped);
      },
      type,
      0.92
    );
  };

  /** The crop, as a background window onto the same image — no second canvas, no second decode. */
  previewStyle() {
    const { url, sel, display } = this.state;
    if (!sel || !display) return { backgroundImage: `url(${url})` };
    const scale = 72 / sel.w;
    return {
      backgroundImage: `url(${url})`,
      backgroundSize: `${display.w * scale}px ${display.h * scale}px`,
      backgroundPosition: `${-sel.x * scale}px ${-sel.y * scale}px`,
    };
  }

  render() {
    const { url, sel } = this.state;
    const L = this.props.labels || {};

    return (
      <Modal
        open
        title={L.cropTitle}
        okText={L.cropConfirm}
        cancelText={L.cropCancel}
        onOk={this.confirm}
        onCancel={this.props.onCancel}
        width={620}
        destroyOnHidden>
        <Flex vertical gap="middle">
          <div className="pt-cropper-stage" onPointerMove={this.onMove} onPointerUp={this.endDrag} onPointerLeave={this.endDrag}>
            <img ref={this.imgRef} src={url} onLoad={this.onImgLoad} className="pt-cropper-img" alt="" draggable={false} />
            {sel && (
              <div
                ref={this.selRef}
                className="pt-cropper-sel"
                style={{ left: sel.x, top: sel.y, width: sel.w, height: sel.h }}
                role="group"
                aria-label={L.cropArea}
                tabIndex={0}
                onKeyDown={this.onKeyDown}
                onPointerDown={e => this.startDrag("move", e)}>
                {["nw", "ne", "sw", "se"].map(hd => (
                  <span key={hd} className={"pt-cropper-handle pt-cropper-handle-" + hd} onPointerDown={e => this.startDrag(hd, e)} />
                ))}
              </div>
            )}
          </div>

          <Flex align="center" gap="middle" wrap>
            <Flex align="center" gap="small" flex="1" className="pt-cropper-zoom">
              <ZoomOutOutlined aria-hidden />
              {/* The slider drives the SIDE LENGTH, inverted: a smaller square shows less of the
                  image, which is what zooming in means when the image itself does not move. */}
              <Slider
                style={{ flex: 1 }}
                min={MIN_SIDE}
                max={Math.max(MIN_SIDE + 1, this.maxSide)}
                value={sel ? this.maxSide + MIN_SIDE - sel.w : this.maxSide}
                disabled={!sel}
                tooltip={{ open: false }}
                aria-label={L.cropSize}
                onChange={value => this.setSide(this.maxSide + MIN_SIDE - value)}
              />
              <ZoomInOutlined aria-hidden />
            </Flex>

            <Flex vertical align="center" gap={4}>
              <div className="pt-cropper-preview" style={this.previewStyle()} role="img" aria-label={L.cropPreview} />
              <Typography.Text type="secondary" className="pt-cropper-caption">
                {L.cropPreview}
              </Typography.Text>
            </Flex>
          </Flex>

          <Typography.Text type="secondary" className="pt-cropper-caption">
            {L.cropHint}
          </Typography.Text>
        </Flex>
      </Modal>
    );
  }
}
